/**
 * The optional exact capture date the API hands back as `recordedDate`, always the plain
 * string "YYYY-MM-DD" or null. It is deliberately not parsed into a Date for display --
 * `new Date('1990-12-24')` is read as UTC midnight and renders as the 23rd for anyone
 * west of Greenwich, which is exactly the kind of off-by-one a family archive notices.
 */

const pattern = /^(\d{4})-(\d{2})-(\d{2})$/;

export function formatRecordedDate(recordedDate) {
  if (!recordedDate) {
    return '';
  }

  const match = recordedDate.match(pattern);
  if (!match) {
    return '';
  }

  return `${match[3]}.${match[2]}.${match[1]}`;
}

/**
 * What every list tile and detail page shows next to the calendar icon: the exact date
 * when it is known, the year alone otherwise. Videos carry `year` as an array (the
 * column is a JSON list, a leftover of the Symfony import), galleries as a number.
 */
export default function recordedDateOrYear(recordedDate, year) {
  const formatted = formatRecordedDate(recordedDate);
  if (formatted !== '') {
    return formatted;
  }

  if (Array.isArray(year)) {
    return year.join('/');
  }

  return year === null || year === undefined ? '' : year.toString();
}

/**
 * "YYYY-MM-DD" out of the Date the picker hands us. toISOString() would shift the day
 * across the UTC boundary, so the local parts are read off directly.
 */
export function toRecordedDateString(date) {
  if (!date || isNaN(date.getTime())) {
    return null;
  }

  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * The inverse, for seeding the picker from a stored value. Built from the parts rather
 * than parsed, for the same timezone reason as above.
 */
export function fromRecordedDateString(recordedDate) {
  if (!recordedDate) {
    return null;
  }

  const match = recordedDate.match(pattern);
  if (!match) {
    return null;
  }

  return new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
}
